import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { ref } from 'lit/directives/ref.js';
import { LibElement, createUniqueId } from '../lib-element';
import { LibActionBarItem } from './lib-action-bar-item';
import css from './lib-action-bar.css';

@customElement('lib-action-bar')
export class LibActionBar extends LibElement {
  protected override get hostFocusSelector(): string { return 'button[data-stop]'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @property() position = 'bottom';
  @property() surface = 'white';
  @property({ attribute: 'label-mode' }) labelMode = 'hidden';
  @property({ attribute: 'selection-mode' }) selectionMode = 'single';
  @property({ type: Boolean, attribute: 'no-deselect' }) noDeselect = false;
  @property({ attribute: 'active-index' }) activeIndex: string | null = null;
  @property({ attribute: 'active-indexes' }) activeIndexes = '[]';
  @property() overflow = 'none';
  @property({ type: Number, attribute: 'max-visible-items' }) maxVisibleItems: number | null = null;
  @property({ attribute: 'more-label' }) moreLabel = 'More actions';
  @property({ type: Boolean }) floating = false;
  @state() private items: LibActionBarItem[] = [];
  @state() private order: LibActionBarItem[] = [];
  @state() private selected: number[] = [];
  @state() private capacity: number | null = null;
  @state() private menuOpen = false;
  @state() private tabStop: LibActionBarItem | 'more' | null = null;
  private readonly forwardedAttributes = new WeakMap<Element, string[]>();
  private readonly menuId = createUniqueId('lib-actionbar-menu');
  private readonly resize = new ResizeObserver(() => this.measure());
  private readonly mutations = new MutationObserver(() => this.syncItems());
  private get vertical() { return this.position === 'left' || this.position === 'right'; }
  private get labels() { return this.vertical ? this.labelMode : 'hidden'; }
  private get visibleCount() {
    let count = this.items.length;
    if (this.overflow === 'auto' && this.capacity !== null && this.capacity < count) count = Math.max(1, this.capacity - 1);
    if (this.maxVisibleItems !== null) count = Math.min(count, Math.max(1, this.maxVisibleItems));
    return count;
  }
  private get visible() { return this.order.slice(0, this.visibleCount); }
  private get hiddenItems() { return this.order.slice(this.visibleCount); }
  private get stops(): (LibActionBarItem | 'more')[] {
    return [...this.visible.filter(item => !item.disabled), ...(this.hiddenItems.length ? ['more' as const] : [])];
  }
  private get effectiveTabStop() { return this.stops.includes(this.tabStop!) ? this.tabStop : this.stops[0]; }
  get value(): string { return JSON.stringify(this.selected); }

  override connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'toolbar');
    this.mutations.observe(this, { childList: true, subtree: true, attributes: true });
    if (this.parentElement) this.resize.observe(this.parentElement);
    document.addEventListener('pointerdown', this.outside, true);
    document.addEventListener('keydown', this.escape, true);
    this.syncItems();
  }
  override disconnectedCallback() {
    super.disconnectedCallback();
    this.mutations.disconnect();
    this.resize.disconnect();
    document.removeEventListener('pointerdown', this.outside, true);
    document.removeEventListener('keydown', this.escape, true);
  }
  protected override willUpdate(changed: PropertyValues) {
    if (changed.has('activeIndex') || changed.has('activeIndexes') || changed.has('selectionMode')) {
      this.syncSelection();
    }
    const orientation = this.vertical ? 'vertical' : 'horizontal';
    if (this.getAttribute('aria-orientation') !== orientation) this.setAttribute('aria-orientation', orientation);
    if (!this.hiddenItems.length) this.menuOpen = false;
  }
  private syncSelection() {
      if (this.selectionMode === 'single') {
        this.selected = this.activeIndex === null || this.activeIndex === '' ? [] : [Number(this.activeIndex)];
      } else {
        try {
          const indexes: unknown = JSON.parse(this.activeIndexes);
          this.selected = Array.isArray(indexes) ? [...new Set(indexes.filter(Number.isInteger))].sort((a, b) => a - b) : [];
        } catch { this.selected = []; }
      }
  }
  protected override updated() { this.measure(); }
  private syncItems() {
    const items = Array.from(this.children).filter((item): item is LibActionBarItem => item instanceof LibActionBarItem);
    this.items = items;
    this.order = [...this.order.filter(item => items.includes(item)), ...items.filter(item => !this.order.includes(item))];
  }
  private measure() {
    if (this.overflow !== 'auto' || this.labels !== 'hidden' || !this.parentElement) return;
    const parent = this.parentElement;
    const style = getComputedStyle(parent);
    const available = this.vertical
      ? parent.clientHeight - Number.parseFloat(style.paddingTop) - Number.parseFloat(style.paddingBottom)
      : parent.clientWidth - Number.parseFloat(style.paddingLeft) - Number.parseFloat(style.paddingRight);
    const bar = this.renderRoot.querySelector<HTMLElement>('.lib-actionbar');
    const button = bar?.querySelector<HTMLButtonElement>('.lib-actionbar-item');
    if (!bar || !button) return;
    const bounds = button.getBoundingClientRect();
    const slot = this.vertical ? bounds.height : bounds.width;
    if (!slot) return;
    const barStyle = getComputedStyle(bar);
    const padding = this.vertical ? Number.parseFloat(barStyle.paddingTop) + Number.parseFloat(barStyle.paddingBottom)
      : Number.parseFloat(barStyle.paddingLeft) + Number.parseFloat(barStyle.paddingRight);
    const gap = Number.parseFloat(this.vertical ? barStyle.rowGap : barStyle.columnGap) || 0;
    const capacity = Math.max(0, Math.floor((available - padding + gap) / (slot + gap)));
    if (capacity !== this.capacity) this.capacity = capacity;
  }
  private readonly outside = (event: PointerEvent) => {
    if (this.menuOpen && !event.composedPath().includes(this)) this.menuOpen = false;
  };
  private readonly escape = (event: KeyboardEvent) => {
    if (this.menuOpen && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeMenu(true);
    }
  };
  private async closeMenu(focus: boolean) {
    this.menuOpen = false;
    if (focus) { await this.updateComplete; this.focusStop('more'); }
  }
  private async openMenu() {
    this.menuOpen = !this.menuOpen;
    await this.updateComplete;
    if (this.menuOpen) this.renderRoot.querySelector<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)')?.focus();
  }
  private focusStop(stop: LibActionBarItem | 'more') {
    this.tabStop = stop;
    const id = stop === 'more' ? 'more' : String(this.items.indexOf(stop));
    this.renderRoot.querySelector<HTMLButtonElement>(`[data-stop="${id}"]`)?.focus();
  }
  private toolbarKey(event: KeyboardEvent, current: LibActionBarItem | 'more') {
    const next = this.vertical ? 'ArrowDown' : 'ArrowRight';
    const previous = this.vertical ? 'ArrowUp' : 'ArrowLeft';
    if (![next, previous, 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const stops = this.stops;
    let index = (stops.indexOf(current) + (event.key === next ? 1 : -1) + stops.length) % stops.length;
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = stops.length - 1;
    this.focusStop(stops[index]);
  }
  private menuKey(event: KeyboardEvent) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = Array.from(this.renderRoot.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)'));
    const index = buttons.indexOf(event.target as HTMLButtonElement);
    let target = (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = buttons.length - 1;
    buttons[target]?.focus();
  }
  private activate(item: LibActionBarItem, event: MouseEvent, menu: boolean) {
    event.stopPropagation();
    if (item.disabled) return;
    item.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true, clientX: event.clientX, clientY: event.clientY,
      ctrlKey: event.ctrlKey, shiftKey: event.shiftKey, altKey: event.altKey, metaKey: event.metaKey, detail: event.detail }));
    const index = this.items.indexOf(item);
    const active = this.selected.includes(index);
    if (!(active && this.noDeselect && (this.selectionMode === 'single' || this.selected.length === 1))) {
      if (this.selectionMode === 'single') this.selected = active ? [] : [index];
      else if (active) this.selected = this.selected.filter(value => value !== index);
      else this.selected = [...this.selected, index].sort((a, b) => a - b);
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }
    if (menu) {
      const order = this.order.filter(value => value !== item);
      order.splice(Math.max(0, this.visibleCount - 1), 0, item);
      this.order = order;
      this.closeMenu(true);
    }
  }
  private forwardAttributes(button: Element | undefined, item: LibActionBarItem, menu: boolean) {
    if (!button) return;
    for (const name of this.forwardedAttributes.get(button) ?? []) button.removeAttribute(name);
    button.setAttribute('aria-label', item.text);
    button.setAttribute('tabindex', !menu && this.effectiveTabStop === item ? '0' : '-1');
    if (menu) {
      button.setAttribute('role', this.selectionMode === 'single' ? 'menuitemradio' : 'menuitemcheckbox');
      button.setAttribute('aria-checked', String(this.selected.includes(this.items.indexOf(item))));
    } else {
      button.setAttribute('aria-pressed', String(this.selected.includes(this.items.indexOf(item))));
    }
    const attributes = Array.from(item.attributes).filter(attribute =>
      !['text', 'icon-url', 'disabled', 'class', 'style'].includes(attribute.name)
      && !attribute.name.startsWith('_bl_'));
    for (const attribute of attributes) button.setAttribute(attribute.name, attribute.value);
    this.forwardedAttributes.set(button, attributes.map(attribute => attribute.name));
  }
  private renderItem(item: LibActionBarItem, menu = false) {
    const index = this.items.indexOf(item);
    const active = this.selected.includes(index);
    const labelClass = this.labels === 'hidden' ? 'lib-actionbar-item-tooltip' : '';
    const menuRole = this.selectionMode === 'single' ? 'menuitemradio' : 'menuitemcheckbox';
    const classes = `lib-actionbar-item ${item.className} ${active ? 'lib-actionbar-item-active' : ''} ${item.disabled ? 'lib-actionbar-item-disabled' : ''} ${menu ? 'lib-actionbar-menu-item' : labelClass}`;
    const icon = item.iconUrl ? html`<lib-icon icon-url=${item.iconUrl}></lib-icon>` : nothing;
    const label = menu || this.labels !== 'hidden' ? html`<span class="lib-actionbar-item-label">${item.text}</span>` : nothing;
    return html`<button ${ref(button => this.forwardAttributes(button, item, menu))} type="button" class=${classes}
      style=${item.getAttribute('style') ?? ''} ?disabled=${item.disabled}
      aria-label=${item.text} aria-pressed=${menu ? nothing : String(active)}
      role=${menu ? menuRole : nothing}
      aria-checked=${menu ? String(active) : nothing}
      data-tooltip=${!menu && this.labels === 'hidden' ? item.text : nothing}
      data-stop=${menu ? nothing : String(index)} tabindex=${!menu && this.effectiveTabStop === item ? 0 : -1}
      @click=${(event: MouseEvent) => this.activate(item, event, menu)}
      @focus=${() => { if (!menu) this.tabStop = item; }}
      @keydown=${(event: KeyboardEvent) => menu ? this.menuKey(event) : this.toolbarKey(event, item)}>
      ${icon}
      ${label}
      <span class="lib-actionbar-item-indicator" aria-hidden="true"></span>
    </button>`;
  }
  override render() {
    const moreTabIndex = this.effectiveTabStop === 'more' ? 0 : -1;
    const moreTooltip = this.menuOpen ? nothing : this.moreLabel;
    const moreClass = `lib-actionbar-item lib-actionbar-more lib-actionbar-item-tooltip ${this.menuOpen ? 'lib-actionbar-item-pressed' : ''}`;
    const classes = `lib-actionbar lib-actionbar-${this.position} lib-actionbar-${this.surface} ${this.floating ? 'lib-actionbar-floating' : ''} ${this.labels === 'expandonhover' ? 'lib-actionbar-expand' : ''}`;
    const more = this.hiddenItems.length ? html`<button type="button" class=${moreClass}
        aria-label=${this.moreLabel} ARIA_HAS_DIALOG="menu" aria-expanded=${String(this.menuOpen)} aria-controls=${this.menuId}
        data-tooltip=${moreTooltip} data-stop="more" tabindex=${moreTabIndex}
        @click=${() => this.openMenu()} @focus=${() => { this.tabStop = 'more'; }}
        @keydown=${(event: KeyboardEvent) => this.toolbarKey(event, 'more')}>
        <lib-icon icon-url=${new URL('../wago-icons-svg/kebab-menu.svg', import.meta.url).href}></lib-icon>
      </button>` : nothing;
    const menu = this.menuOpen ? html`<div id=${this.menuId} class="lib-actionbar-menu" role="menu" aria-orientation="vertical">
        ${repeat(this.hiddenItems, item => item, item => this.renderItem(item, true))}
      </div>` : nothing;
    return html`<slot @slotchange=${this.syncItems} @lib-action-item-change=${this.syncItems}></slot><div class=${classes}>
      ${repeat(this.visible, item => item, item => this.renderItem(item))}
      ${more}
      ${menu}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-action-bar': LibActionBar; }
}
